import { Flex } from 'antd';

import { EntityLevel } from '@suntex.fim/enums';
import { useOptionalSchoolContext } from '@suntex.fim/school';
import { useOptionalTeamContext } from '@suntex.fim/team';
import { Dropdown } from '@suntex.fim/ui';

import { useEntityContext } from './context/EntityContext';

interface EntityOption {
  id: number;
  label: string;
}

/**
 * Header control that lets team leaders, principals and players switch between
 * the entities linked to their login
 */
export function EntitySwitcher() {
  const { entityLevel, entityId, switchEntity } = useEntityContext();
  const teamCtx = useOptionalTeamContext();
  const schoolCtx = useOptionalSchoolContext();

  const schoolInfo = teamCtx?.schoolInfo ?? schoolCtx?.schoolInfo ?? null;

  // Prefer the team leader's own linked teams; fall back to the school's
  // linked teams when no team context is available (e.g. for principals).
  const teamOptions: EntityOption[] = teamCtx?.myTeams.length
    ? teamCtx.myTeams.map(({ id, name, className }) => ({
        id,
        label: `${className} (${name ?? id})`,
      }))
    : schoolInfo
      ? schoolInfo.linkedTeams.map(({ id, teamName, className }) => ({
          id,
          label: `${className} (${teamName})`,
        }))
      : [];

  const schoolOptions: EntityOption[] = schoolInfo
    ? schoolInfo.linkedSchools.map(({ id, schoolName }) => ({
        id,
        label: schoolName,
      }))
    : [];

  const activeTeamId =
    entityLevel === EntityLevel.Team ? entityId : teamOptions[0]?.id;
  const activeTeamLabel =
    teamOptions.find(({ id }) => id === activeTeamId)?.label ??
    teamOptions[0]?.label;

  const activeSchoolId =
    entityLevel === EntityLevel.School ? entityId : schoolOptions[0]?.id;
  const activeSchoolLabel =
    schoolOptions.find(({ id }) => id === activeSchoolId)?.label ??
    schoolOptions[0]?.label;

  return (
    <Flex gap={10} wrap>
      {schoolOptions.length > 1 && (
        <Flex align="center" gap={5}>
          <p>School:</p>
          <Dropdown
            ariaLabel="Switch school"
            menu={{
              items: schoolOptions.map(({ id, label }) => ({
                key: id,
                label,
                onClick: () => switchEntity(EntityLevel.School, id),
              })),
            }}
          >
            {activeSchoolLabel}
          </Dropdown>
        </Flex>
      )}
      {teamOptions.length > 1 && (
        <Flex align="center" gap={5}>
          <p>Team:</p>
          <Dropdown
            ariaLabel="Switch team"
            menu={{
              items: teamOptions.map(({ id, label }) => ({
                key: id,
                label,
                onClick: () => switchEntity(EntityLevel.Team, id),
              })),
            }}
          >
            {activeTeamLabel}
          </Dropdown>
        </Flex>
      )}
    </Flex>
  );
}
